import { useNavigate, useParams } from 'react-router-dom';
import {
  AssetFileModel,
  SystemTypePpu,
  useGetSingleAssetsQuery,
} from '@customer/store/apis/assetsApi';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetConfigurationGroupName } from '@customer/store/apis/configurationGroupsApi';
import InformationSection from '@customer/routes/asset/components/info-section/InformationSection';
import { GridControlled } from '@customer/components/tables/grid/GridControlled';
import { useTranslation } from 'react-i18next';
import { useEffect, useMemo } from 'react';
import { AccordionStatus } from '@customer/components/accordions/types';
import {
  GeofenceData,
  useAssetGeofenceTableData,
} from '@customer/hooks/assets/useAssetGeofenceTableData';
import SingleAssetSubPage from '@customer/templates/single-asset-sub-page/SingleAssetSubPage';
import { AssetNameData, useAssetNameTableData } from '@customer/hooks/assets/useAssetNameTableData';
import {
  columnDefAssetFilesApplied,
  columnDefAssetFilesStatus,
  columnDefAssetNamesName,
  columnDefAssetNamesSource,
  lockColumn,
} from '@customer/components/tables/grid/columnDefs';
import { ColDef, RowClassParams, RowClassRules } from 'ag-grid-community';
import { useAbsoluteTimePartialColDef } from '@customer/components/tables/cells/absolute-time/useAbsoluteTime';
import { useAssetGeofenceColDefs } from '@customer/components/asset-data/geofence-status/useAssetGeofenceColDefs';

const GridSliceKeyFiles = 'AssetFiles';
const GridSliceKeyGeofences = 'AssetGeofences';
const GridSliceKeyNameMismatch = 'AssetNameMismatch';

const getRowIdPackages = (data: AssetFileModel) => data.name;
const getRowIdGeofences = (data: GeofenceData) => data.where;
const getRowIdAssetName = (data: AssetNameData) => data.name;

const filesRowClassRules: RowClassRules<AssetFileModel> = {
  'dm-bold-row': (params: RowClassParams<AssetFileModel>) => !params.data?.app,
};

const geofenceRowClassRules: RowClassRules<GeofenceData> = {
  'dm-bold-row': (params: RowClassParams<GeofenceData>) => Boolean(params.data?.error),
};

export function SingleAssetConfiguration() {
  const { assetId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { currentData: asset } = useGetSingleAssetsQuery(assetId ?? skipToken, {
    refetchOnMountOrArgChange: 10,
  });
  const configGroupName = useGetConfigurationGroupName(asset?.cfgGrp);

  useEffect(() => {
    if (asset && asset.hw === SystemTypePpu) {
      // PPUs don't have configuration or diagnostics, so switch to the appropriate tab
      navigate(`/asset/${asset.id}/details`);
    }
  }, [asset, navigate]);

  const headerItems = useMemo(() => {
    return [
      { titleTransId: 'DeviceID', value: asset?.did },
      { titleTransId: 'Description', value: asset?.desc },
      { titleTransId: 'ConfigurationGroup', value: configGroupName ?? '' },
      { titleTransId: 'Updated', value: asset?.tsCfg, isTime: true },
    ];
  }, [asset?.desc, asset?.did, asset?.tsCfg, configGroupName]);

  const absoluteTimeColDef = useAbsoluteTimePartialColDef<AssetFileModel>();

  const columnDefsPackages = useMemo<ColDef<AssetFileModel>[]>(
    () => [
      { field: 'name', headerName: t('PackageName') },
      { field: 'ver', headerName: t('File Version') },
      {
        field: 'rep',
        headerName: t('Report Time'),
        ...absoluteTimeColDef,
      },
      {
        field: 'act',
        headerName: t('Activation Time'),
        ...absoluteTimeColDef,
      },
      { ...columnDefAssetFilesApplied(t) },
      { ...columnDefAssetFilesStatus(t), suppressSizeToFit: true },
    ],
    [t, absoluteTimeColDef],
  );

  const geofenceData = useAssetGeofenceTableData({ asset });

  const columnDefsGeofences = useAssetGeofenceColDefs();

  const assetNameData = useAssetNameTableData({ asset });

  const columnDefsAssetNames: ColDef<AssetNameData>[] = useMemo(
    () => [{ ...columnDefAssetNamesName(t) }, { ...columnDefAssetNamesSource(t) }],
    [t],
  );

  return (
    <SingleAssetSubPage headerItems={headerItems}>
      <InformationSection
        status={asset && asset.stsCfg ? AccordionStatus.Healthy : AccordionStatus.Danger}
        icon={asset && asset.stsCfg ? 'good' : 'caution'}
        title={t('SoftwareStatus')}
      >
        <GridControlled<AssetFileModel>
          sliceKey={GridSliceKeyFiles}
          rowData={asset?.files}
          columnDefs={columnDefsPackages}
          getRowId={getRowIdPackages}
          rowClassRules={filesRowClassRules}
          noRowsMessage={t('No files available')}
          autoheight
        />
      </InformationSection>

      <InformationSection
        status={asset && asset.stsGeo ? AccordionStatus.Healthy : AccordionStatus.Danger}
        icon={asset && asset.stsGeo ? 'good' : 'information'}
        title={t('GeofenceStatus')}
      >
        <GridControlled<GeofenceData>
          sliceKey={GridSliceKeyGeofences}
          rowData={geofenceData}
          columnDefs={columnDefsGeofences}
          defaultColDef={lockColumn}
          getRowId={getRowIdGeofences}
          rowClassRules={geofenceRowClassRules}
          autoheight
        />
      </InformationSection>

      {asset && !asset.stsNam && (
        <InformationSection
          status={AccordionStatus.Danger}
          icon="information"
          title={t('AssetNameMismatch')}
        >
          <GridControlled<AssetNameData>
            sliceKey={GridSliceKeyNameMismatch}
            rowData={assetNameData}
            columnDefs={columnDefsAssetNames}
            defaultColDef={lockColumn}
            getRowId={getRowIdAssetName}
            autoheight
          />
        </InformationSection>
      )}
    </SingleAssetSubPage>
  );
}

export default SingleAssetConfiguration;
